import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import { useTheme, alpha } from "@mui/material/styles";

import BoltIcon from "@mui/icons-material/Bolt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { shortcuts } from "./shortcutData";

export default function ShortcutsList() {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: "#ffffff",
            }}
        >
            {/* Navigation Bar - match Landing */}
            <Box
                sx={{
                    pt: 5,
                    px: { xs: 0, sm: 4 },
                    backgroundColor: "#ffffff",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component={RouterLink}
                            to="/"
                            sx={{ display: "inline-block" }}
                        >
                            <Box
                                component="img"
                                src="/tapsheet_logo_text.png"
                                alt="TapSheet"
                                sx={{
                                    height: { xs: "24px", sm: "28px" },
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Back to Home
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 }, pb: 6 }}>
                <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: "1.75rem", md: "2.125rem" },
                            color: "text.primary",
                        }}
                    >
                        Shortcut Library
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: 480 }}
                    >
                        Add these shortcuts to your iPhone to log expenses faster and automate TapSheet.
                    </Typography>
                </Stack>

                <Stack spacing={2}>
                    {shortcuts.map((shortcut) => (
                        <Card
                            key={shortcut.slug}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: "hidden",
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                },
                            }}
                        >
                            <CardActionArea
                                onClick={() => navigate(`/shortcuts/${shortcut.slug}`)}
                                sx={{ display: "block", textAlign: "left" }}
                            >
                                <CardContent sx={{ py: 2.5, px: 3 }}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={2}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 2,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <BoltIcon
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        fontSize: 26,
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: "1.1rem",
                                                    }}
                                                >
                                                    {shortcut.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.25,
                                                        lineHeight: 1.4,
                                                        display: { xs: "none", sm: "block" },
                                                    }}
                                                >
                                                    {shortcut.description}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <ChevronRightIcon
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: 28,
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Stack>
            </Container>

            {/* Footer - match Landing */}
            <Box
                sx={{
                    py: 3,
                    px: { xs: 2, sm: 4 },
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "background.paper",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/privacy"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Privacy Policy
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/terms"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Terms of Service
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/support"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Support
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
